import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import Login from "./pages/Login";
import HomeFeed from "./pages/HomeFeed";
import Layout from "./components/Layout";
import PulseLogo from "./components/PulseLogo";
import { API } from "./lib/api";
import { ChatProvider } from "./lib/chatContext";
import "./App.css";

// Code splitting: the entry experience (Login → Home) is eager so first paint
// is instant. Every other page is lazy-loaded — the bundle for ChatPage,
// AdminDashboard, Inventory etc. only downloads when the user navigates
// there. This drops first-load JS by ~60% on mobile.
const ChatPage = lazy(() => import("./pages/ChatPage"));
const ActionCenter = lazy(() => import("./pages/ActionCenter"));
const BusinessInsights = lazy(() => import("./pages/BusinessInsights"));
const Inventory = lazy(() => import("./pages/Inventory"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const ZeroPriceInvoices = lazy(() => import("./pages/ZeroPriceInvoices"));
const OnTheWay = lazy(() => import("./pages/OnTheWay"));
const FamilyProfitability = lazy(() => import("./pages/FamilyProfitability"));
const SmartRestock = lazy(() => import("./pages/SmartRestock"));
const WhatChanged = lazy(() => import("./pages/WhatChanged"));
const CustomerChurn = lazy(() => import("./pages/CustomerChurn"));

// Lightweight fallback for lazy chunks — branded pulse logo with a soft
// fade so it doesn't feel like a "loading spinner" between pages.
function PageFallback() {
  return (
    <div
      className="flex items-center justify-center py-32"
      data-testid="page-loading"
    >
      <div className="opacity-60 animate-pulse">
        <PulseLogo size={32} />
      </div>
    </div>
  );
}

function RequireAuth({ children }) {
  const token = localStorage.getItem("pulse_token");
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  // Pre-warm the backend the moment the SPA mounts. Production containers
  // can sleep when idle; this fires a cheap, public diag call so the worker
  // is already loading sales.json into pandas by the time the user hits
  // "הראה לי". Without this, the first-paint feels broken for ~10s.
  useEffect(() => {
    fetch(`${API}/diag/data`, { method: "GET" }).catch(() => {});
  }, []);

  return (
    <div className="App min-h-screen bg-obsidian">
      <Toaster
        theme="dark"
        position="bottom-left"
        toastOptions={{
          style: { background: "#0A0A0E", border: "1px solid #27272a", color: "#e4e4e7", fontSize: "13px" },
        }}
      />
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
        <ChatProvider>
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/"
                element={
                  <RequireAuth>
                    <Layout />
                  </RequireAuth>
                }
              >
                <Route index element={<HomeFeed />} />
                <Route path="insights" element={<BusinessInsights />} />
                <Route path="inventory" element={<Inventory />} />
                <Route path="chat" element={<ChatPage />} />
                <Route path="actions" element={<ActionCenter />} />
                <Route path="admin" element={<AdminDashboard />} />
                <Route path="zero-price" element={<ZeroPriceInvoices />} />
                <Route path="on-the-way" element={<OnTheWay />} />
                <Route path="families" element={<FamilyProfitability />} />
                <Route path="smart-restock" element={<SmartRestock />} />
                <Route path="what-changed" element={<WhatChanged />} />
                <Route path="customers" element={<CustomerChurn />} />
                <Route path="customers/churn" element={<CustomerChurn />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </ChatProvider>
      </BrowserRouter>
      </QueryClientProvider>
    </div>
  );
}

export default App;
